import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState, forwardRef } from 'react';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { components } from 'react-select';
import { Form } from 'react-bootstrap';

const LibSelect = dynamic(() => import('react-select'), {
  ssr: false,
});

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledGroup = styled(Form.Group)`
  min-width: 200px;
`;

const StyledSelectContainer = styled.div`
  /* min-height: 52px; */

  padding-top: ${spaces.smallX}px;
  padding-bottom: ${spaces.smallX}px;
  padding-left: ${spaces.regular}px;
  padding-right: ${spaces.regular}px;

  border: 1px solid ${colors.gray[200]};
  border-radius: 8px;
  background-color: ${colors.white};

  .base-select-container {
    .base-select__control {
      min-height: unset;
      border: none;
      background: none;
      cursor: pointer;

      .base-select__indicator-separator {
        display: none;
      }

      .base-select__indicator {
        &.base-select__dropdown-indicator {
          pointer-events: none;
        }
      }

      .base-select__indicator {
        padding: 0;
      }

      .base-select__value-container {
        padding: 0;

        .base-select__input-container {
          height: 26px;

          padding: 0;
          margin: 0;

          font-family: 'Aeonik';
          font-style: normal;
          font-weight: 400;
          font-size: ${fontSizes.regular}px;
          line-height: 150%;
          color: ${colors.gray[800]};

          input {
            height: 26px;
          }
        }

        .base-select__single-value {
          margin: 0;
          font-family: 'Aeonik';
          font-size: ${fontSizes.regular}px;
          font-style: normal;
          font-weight: 400;
          line-height: 140%;
          color: ${colors.gray[800]};
        }

        .base-select__placeholder {
          margin: 0;
          font-family: 'Aeonik';
          font-size: ${fontSizes.regular}px;
          font-style: normal;
          font-weight: 400;
          line-height: 140%;
          color: ${colors.gray[500]};
        }
      }

      &.base-select__control--is-focused {
        box-shadow: none;
        border-color: ${colors.indigo[500]};
      }
    }

    .base-select__menu {
      min-width: 200px;
      right: 0;

      .base-select__menu-list {
        .base-select__option {
          font-family: 'Aeonik';
          font-style: normal;
          font-weight: 400;
          font-size: ${fontSizes.regular}px;
          line-height: 150%;
          color: ${colors.gray[700]};

          cursor: pointer;

          &.base-select__option--is-focused {
            background-color: ${colors.indigo[200]};
          }

          &.base-select__option--is-selected {
            background-color: ${colors.indigo[500]};
          }
        }
      }
    }
  }

  ${({ open }) =>
    open &&
    css`
      border-color: ${colors.indigo[500]};
    `}
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Select = ({
  name,
  placeholder,
  options = [],
  value,
  onChange,
  onBlur,
  ...restProps
}) => {
  const [open, setOpen] = useState(false);
  const [innerOptions, setInnerOptions] = useState([]);

  const currentValue = useMemo(
    () => innerOptions?.find((option) => option?.value === value),
    [value, innerOptions]
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    onBlur && onBlur({ target: { name } });
  }, [name, onBlur]);

  const handleChange = useCallback(
    (option) => {
      onChange && onChange({ target: { name, value: option?.value || '' } });
    },
    [onChange, name]
  );

  useEffect(() => {
    setInnerOptions(
      options.map(({ value, title }) => {
        return { value, label: title };
      })
    );
  }, [options]);

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {open && (
          <StyledIconContainer>
            <Icon alias={ICONS_ALIASES.chevronUp} color={colors.gray[500]} />
          </StyledIconContainer>
        )}
        {!open && (
          <StyledIconContainer>
            <Icon alias={ICONS_ALIASES.chevronDown} color={colors.gray[500]} />
          </StyledIconContainer>
        )}
      </components.DropdownIndicator>
    );
  };

  return (
    <StyledGroup>
      <StyledSelectContainer open={open}>
        <LibSelect
          className="base-select-container"
          classNamePrefix="base-select"
          components={{ DropdownIndicator }}
          options={innerOptions}
          placeholder={placeholder}
          value={currentValue}
          onChange={handleChange}
          onMenuOpen={handleOpen}
          onMenuClose={handleClose}
        />
      </StyledSelectContainer>
      <Form.Control readOnly hidden name={name} {...restProps} />
    </StyledGroup>
  );
};

Select.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

export default Select;
